import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { supabase, wasPasswordRecoveryLink } from '@/lib/supabaseClient'
import { useAuthStore } from '@/stores/authStore'
import LogoSpinner from '@/components/ui/LogoSpinner'

type Phase = 'verifying' | 'allowed' | 'denied' | 'error' | 'profile-stalled'

// If verifyOtp succeeded but the profile fetch hasn't populated `user`
// within this window, surface a Try-Again panel instead of leaving the user
// staring at a forever-spinner. Tuned to comfortably exceed the axios 30s
// timeout + 2s retry backoff in apiService / authStore (~32s worst case).
const PROFILE_STALL_MS = 35_000

/**
 * Lands here when a user clicks the magic link in their email. We support
 * three URL shapes Supabase can produce, in order of preference:
 *
 *   1. ?token_hash=…&type=magiclink   — the recommended flow (configured via
 *      the email template using {{ .TokenHash }}). Survives email-scanner
 *      prefetching because the token isn't consumed until JS calls verifyOtp.
 *   2. ?code=…                        — PKCE code-exchange flow.
 *   3. #access_token=…                → implicit flow (auto-handled by
 *      detectSessionInUrl on client init).
 *
 * After a session is established we run /auth/me (via refreshProfile) so the
 * allowlist check fires. If the member has no password set on their Supabase
 * account yet (user_metadata.passwordSet !== true), we send them to
 * /set-password so they can pick one for future logins.
 */
const AuthCallbackPage = () => {
  const [phase, setPhase] = useState<Phase>('verifying')
  const [errorMessage, setErrorMessage] = useState('')
  const [retrying, setRetrying] = useState(false)
  const { status, refreshProfile, user, supabaseUser } = useAuthStore()
  const verifiedRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    const finish = async () => {
      try {
        const url = new URL(window.location.href)
        const tokenHash = url.searchParams.get('token_hash')
        const type = url.searchParams.get('type')
        const code = url.searchParams.get('code')
        const errorDescription =
          url.searchParams.get('error_description') ||
          new URLSearchParams(window.location.hash.replace(/^#/, '')).get('error_description')

        // Supabase passes auth errors back via query/hash params — surface them
        // verbatim so we don't masquerade a real expiry as a generic failure.
        if (errorDescription) {
          throw new Error(decodeURIComponent(errorDescription.replace(/\+/g, ' ')))
        }

        // 1. Token-hash flow (recommended). The link came directly to our app,
        //    so the token is still valid even if email scanners GET'd the URL.
        if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as 'magiclink' | 'email' | 'recovery' | 'invite' | 'signup' | 'email_change',
          })
          if (error) throw error
        }
        // 2. PKCE code-exchange flow. detectSessionInUrl normally handles this
        //    on client init, but explicit exchange surfaces clearer errors and
        //    works even if init-time exchange race-conditioned.
        else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) throw error
        }
        // 3. Implicit-flow / pre-detected session — fall through to getSession.
        else {
          const { data, error } = await supabase.auth.getSession()
          if (error) throw error
          if (!data.session) {
            setPhase('error')
            setErrorMessage(
              'Your sign-in link is invalid or expired. ' +
                "If you opened the link on a different browser than where you requested it, " +
                "or if your inbox's link scanner consumed the link first, request a new one."
            )
            return
          }
        }

        // Strip auth tokens from the URL bar so they don't show up in browser
        // history / shoulder-surfing.
        window.history.replaceState({}, document.title, '/auth/callback')

        verifiedRef.current = true
        await refreshProfile()
        if (cancelled) return
      } catch (err: any) {
        if (cancelled) return
        setPhase('error')
        setErrorMessage(err?.message || 'Sign-in failed. Please try again.')
      }
    }
    finish()
    return () => {
      cancelled = true
    }
  }, [refreshProfile])

  // If verifyOtp succeeded but refreshProfile silently failed (e.g. axios
  // timeout against a still-cold backend), `status` becomes 'authenticated'
  // and `user` stays null. Without this watchdog we'd sit on the spinner
  // forever waiting for a state change that never comes.
  useEffect(() => {
    if (phase !== 'verifying') return
    if (!verifiedRef.current) return
    const t = setTimeout(() => {
      if (verifiedRef.current && !useAuthStore.getState().user) {
        setPhase('profile-stalled')
      }
    }, PROFILE_STALL_MS)
    return () => clearTimeout(t)
  }, [phase])

  // React to store changes after refreshProfile resolves.
  useEffect(() => {
    if (status === 'authenticated' && user) {
      setPhase('allowed')
    } else if (status === 'denied') {
      setPhase('denied')
    }
  }, [status, user])

  const handleRetryProfile = async () => {
    setRetrying(true)
    setPhase('verifying')
    try {
      await refreshProfile()
    } finally {
      setRetrying(false)
    }
  }

  if (phase === 'allowed') {
    // Arrived via a password-reset email → drop them on the Security tab to
    // choose a new password (no current-password step needed).
    if (wasPasswordRecoveryLink()) {
      return <Navigate to="/profile?tab=security&reset=1" replace />
    }
    const passwordSet = supabaseUser?.user_metadata?.['passwordSet'] === true
    return <Navigate to={passwordSet ? '/dashboard' : '/set-password'} replace />
  }
  if (phase === 'denied') {
    return <Navigate to="/access-denied" replace />
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white p-6">
      <div className="text-center max-w-md">
        {phase === 'verifying' && (
          <>
            <div className="mb-5 flex justify-center">
              <LogoSpinner size="xl" />
            </div>
            <h1 className="serif-font text-2xl font-semibold mb-2">Signing you in…</h1>
            <p className="text-blue-100/80 text-sm">Verifying your link with Shorewood.</p>
          </>
        )}
        {phase === 'profile-stalled' && (
          <>
            <h1 className="serif-font text-2xl font-semibold mb-3">Almost there</h1>
            <p className="text-blue-100/80 text-sm mb-6">
              Your sign-in worked, but the Shorewood server is taking a moment to wake up.
              Try once more — it usually responds quickly the second time.
            </p>
            <button
              type="button"
              onClick={handleRetryProfile}
              disabled={retrying}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition shadow-lg disabled:opacity-60"
            >
              {retrying ? 'Trying again…' : 'Try again'}
            </button>
          </>
        )}
        {phase === 'error' && (
          <>
            <h1 className="serif-font text-2xl font-semibold mb-3">We couldn't sign you in</h1>
            <p className="text-blue-100/80 text-sm mb-6">{errorMessage}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition shadow-lg"
            >
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

export default AuthCallbackPage
